/**
 * `<CrossJdModal>` — "Also consider for another role" JD picker.
 *
 * Spec: candidate-detail-rebuild-spec.md §3.2.
 *
 * - Shows all JDs in the tenant.
 * - JDs the candidate is ALREADY in: disabled with "[Already in pipeline]".
 * - `active` JDs: selectable.
 * - `draft` / `paused` JDs: disabled with "Activate first" hint.
 * - `archived` JDs: hidden entirely.
 * - On confirm: fires `onAdd(selectedJdId)` and stays open until the
 *   parent closes it (mutation pending → loading state).
 * - Copy per ratification: toast "Added to {JD title}. Evaluation in
 *   progress…" is emitted by the parent after the mutation resolves.
 */
import { useState, type JSX } from "react";
import { Button } from "@/components/common/Button";
import type { Jd } from "@/hooks/queries/candidates/useJd";

interface CrossJdModalProps {
  candidateName: string;
  jds: Jd[];
  /** Set of JD IDs the candidate is already paired with. */
  pairedJdIds: ReadonlySet<string>;
  isPending: boolean;
  onAdd: (jdId: string, jdTitle: string) => void;
  onCancel: () => void;
}

export function CrossJdModal({
  candidateName,
  jds,
  pairedJdIds,
  isPending,
  onAdd,
  onCancel,
}: CrossJdModalProps): JSX.Element {
  const [selectedId, setSelectedId] = useState<string>("");

  const firstName = candidateName.split(" ")[0] ?? candidateName;

  // Filter: hide archived; sort by name.
  const visibleJds = jds
    .filter((j) => j.status !== "archived")
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedJd = visibleJds.find((j) => j.id === selectedId);
  const canSubmit = !!selectedId && !pairedJdIds.has(selectedId) && !isPending;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cross-jd-modal-title"
      onKeyDown={handleKeyDown}
      onClick={onCancel}
      data-testid="cross-jd-modal"
    >
      <div
        className="w-full max-w-md rounded-xl border border-border-default bg-surface-elevated p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="cross-jd-modal-title"
          className="mb-1 text-lg font-semibold text-text-emphasis"
        >
          Also consider {firstName} for another role
        </h2>
        <p className="mb-5 text-sm text-text-secondary">
          Select a job to add {firstName} to its pipeline.
        </p>

        {/* JD picker */}
        <div
          className="mb-4 max-h-64 overflow-y-auto rounded-lg border border-border-default"
          role="listbox"
          aria-label="Select a job"
        >
          {visibleJds.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-text-tertiary">
              No other active jobs available.
            </p>
          ) : (
            visibleJds.map((jd) => {
              const alreadyPaired = pairedJdIds.has(jd.id);
              const isDisabled = alreadyPaired || jd.status !== "active";
              const isSelected = jd.id === selectedId;

              let hint: string | null = null;
              if (alreadyPaired) hint = "Already in pipeline";
              else if (jd.status === "draft" || jd.status === "paused")
                hint = "Activate first";

              return (
                <button
                  key={jd.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && setSelectedId(jd.id)}
                  className={
                    "flex w-full items-center justify-between px-4 py-3 text-left transition-colors " +
                    (isDisabled
                      ? "cursor-not-allowed opacity-50"
                      : isSelected
                        ? "bg-surface-subtle"
                        : "hover:bg-surface-card-hover") +
                    " border-b border-border-default last:border-b-0"
                  }
                  data-testid={`cross-jd-option-${jd.id}`}
                >
                  <span className="text-sm font-medium text-text-primary">
                    {jd.name}
                  </span>
                  {hint ? (
                    <span className="ml-2 flex-shrink-0 text-xs text-text-tertiary">
                      {hint}
                    </span>
                  ) : isSelected ? (
                    <span className="ml-2 flex-shrink-0 text-xs font-medium text-accent-secondary">
                      Selected
                    </span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>

        {/* Context copy when a JD is selected */}
        {selectedJd ? (
          <p className="mb-5 text-sm text-text-secondary">
            CertAIn will add {firstName} to the{" "}
            <strong className="font-medium text-text-primary">
              {selectedJd.name}
            </strong>{" "}
            pipeline. Notes from the current job won&apos;t carry over.
          </p>
        ) : null}

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            autoFocus
            data-testid="cross-jd-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={isPending}
            onClick={() => {
              if (selectedJd) onAdd(selectedJd.id, selectedJd.name);
            }}
            data-testid="cross-jd-confirm"
          >
            {selectedJd ? `Add to ${selectedJd.name} →` : "Add to job →"}
          </Button>
        </div>
      </div>
    </div>
  );
}
